import { Auth } from 'aws-amplify'
import { decodeToken } from 'react-jwt'
import { AMPLIFY_USER_POOL_APP_CLIENT_ID } from 'src/config/config'
import { refreshTokenApi } from 'src/pages/api/sso/api'

let refreshPromise: Promise<string | null> | null = null
let isRefreshing = false
const appClientId = AMPLIFY_USER_POOL_APP_CLIENT_ID

const isSSO = () => {
  try {
    return typeof window !== 'undefined' && localStorage.getItem('sso') === 'true'
  } catch {
    return false
  }
}

export const getStoredIdToken = (): string | null => {
  try {
    if (typeof window === 'undefined') return null

    if (isSSO()) return localStorage.getItem('idToken')

    if (!appClientId) return null

    const key = Object.keys(localStorage).find(
      (k) => k.includes('CognitoIdentityServiceProvider') && k.includes(appClientId) && k.endsWith('.idToken'),
    )
    return key ? localStorage.getItem(key) : null
  } catch {
    return null
  }
}

const isExpiredOrExpiringSoon = (token: string | null, skewSeconds = 300): boolean => {
  if (!token) return true
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const decoded = decodeToken(token) as any
    const exp = decoded?.exp
    if (!exp) return true
    const now = Date.now() / 1000
    return exp < now + skewSeconds
  } catch {
    return true
  }
}

const refreshSSO = async (): Promise<string | null> => {
  try {
    const res = await refreshTokenApi()
    if (!res?.id_token) return null
    localStorage.setItem('idToken', res.id_token)
    localStorage.setItem('accessToken', res.access_token)
    // Update refresh token if a new one is provided (some OAuth2 implementations rotate refresh tokens)
    if (res.refresh_token) {
      localStorage.setItem('refreshToken', res.refresh_token)
    }
    // Note: Token refresh doesn't require cross-tab reload - each tab refreshes its own token when needed
    return res.id_token
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error in refreshSSO:', error)
    return null
  }
}

const refreshCognito = async (): Promise<string | null> => {
  try {
    const user = await Auth.currentAuthenticatedUser()
    const currentSession = user.signInUserSession

    if (!appClientId) return null

    return new Promise((resolve) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      user.refreshSession(currentSession.refreshToken, (err: any, session: any) => {
        if (err) {
          // eslint-disable-next-line no-console
          console.error('Error refreshing Cognito session:', err)
          return resolve(null)
        }

        if (!session?.idToken?.jwtToken) return resolve(null)

        const idToken = session.idToken.jwtToken

        localStorage.setItem(`CognitoIdentityServiceProvider.${appClientId}.${user.username}.idToken`, idToken)
        localStorage.setItem(
          `CognitoIdentityServiceProvider.${appClientId}.${user.username}.accessToken`,
          session.accessToken.jwtToken,
        )
        localStorage.setItem(
          `CognitoIdentityServiceProvider.${appClientId}.${user.username}.refreshToken`,
          session.refreshToken.token,
        )

        // Note: Token refresh doesn't require cross-tab reload - each tab refreshes its own token when needed
        resolve(idToken)
      })
    })
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error in refreshCognito:', error)
    return null
  }
}

export const ensureValidIdToken = async (): Promise<string | null> => {
  const current = getStoredIdToken()

  if (!isExpiredOrExpiringSoon(current)) {
    return current
  }

  // DUAL GUARD - Check both flags
  if (refreshPromise || isRefreshing) {
    if (refreshPromise) {
      return refreshPromise
    }
    // Wait if isRefreshing but refreshPromise not set yet
    return new Promise((resolve) => {
      let isResolved = false
      let checkInterval: NodeJS.Timeout | null = null
      let timeoutId: NodeJS.Timeout | null = null

      const cleanup = () => {
        if (!isResolved) {
          isResolved = true
          if (checkInterval) clearInterval(checkInterval)
          if (timeoutId) clearTimeout(timeoutId)
        }
      }

      checkInterval = setInterval(() => {
        if (refreshPromise) {
          cleanup()
          resolve(refreshPromise)
        }
      }, 10)

      // Increased timeout for slow networks (5 seconds)
      timeoutId = setTimeout(() => {
        cleanup()
        resolve(null)
      }, 5000)
    })
  }

  // Start refresh
  isRefreshing = true
  refreshPromise = (async () => {
    try {
      const refreshedToken = isSSO() ? await refreshSSO() : await refreshCognito()

      // If refresh failed (returned null), trigger logout
      // This ensures logout happens even when calling non-auth GraphQL APIs
      if (!refreshedToken && typeof window !== 'undefined') {
        // Clear tokens immediately so header updates and other tabs detect logout
        if (isSSO()) {
          localStorage.removeItem('idToken')
          localStorage.removeItem('accessToken')
          localStorage.removeItem('refreshToken')
        } else {
          // Non-SSO: remove Cognito-prefixed keys to fire storage events
          if (appClientId) {
            Object.keys(localStorage)
              .filter(
                (k) =>
                  k.includes('CognitoIdentityServiceProvider') &&
                  k.includes(appClientId) &&
                  (k.endsWith('.idToken') || k.endsWith('.accessToken') || k.endsWith('.refreshToken')),
              )
              .forEach((key) => localStorage.removeItem(key))
          }
        }

        // Dispatch custom event for same-tab Redux update (storage event only fires in other tabs)
        // The event listener in _app.tsx will handle Redux dispatch
        window.dispatchEvent(new Event('auth:logout'))
      }

      return refreshedToken
    } catch {
      // On error, also trigger logout
      if (typeof window !== 'undefined') {
        if (isSSO()) {
          localStorage.removeItem('idToken')
          localStorage.removeItem('accessToken')
          localStorage.removeItem('refreshToken')
        } else {
          if (appClientId) {
            Object.keys(localStorage)
              .filter(
                (k) =>
                  k.includes('CognitoIdentityServiceProvider') &&
                  k.includes(appClientId) &&
                  (k.endsWith('.idToken') || k.endsWith('.accessToken') || k.endsWith('.refreshToken')),
              )
              .forEach((key) => localStorage.removeItem(key))
          }
        }
        window.dispatchEvent(new Event('auth:logout'))
      }
      return null
    } finally {
      isRefreshing = false
      // Clear the promise after completion so subsequent calls can refresh again if needed
      refreshPromise = null
    }
  })()

  return refreshPromise
}

export const resetTokenRefreshGuard = () => {
  refreshPromise = null
  isRefreshing = false
}
